import { Target, Users, BookOpen, LineChart, Network, Workflow, Database, GitMerge } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";

const HR = () => {
  const { t } = useTranslation(["hr"]);

  const gpecBenefits = t('features.gpec.benefits', { returnObjects: true }) as string[];
  const rseBenefits = t('features.rse.benefits', { returnObjects: true }) as string[];

  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/8),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.orange/4),transparent)]" />
          <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/5),transparent)] dark:bg-[radial-gradient(45rem_50rem_at_top_right,theme(colors.brand.blue/3),transparent)]" />
        </div>
        
        <div className="container-custom relative z-10 py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/10 dark:bg-brand-blue/5 text-brand-blue dark:text-brand-orange mb-8">
              <Target className="w-5 h-5" />
              <span className="text-sm font-medium">{t('badge')}</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange leading-[1.2] pb-4">
              {t('title')}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact?type=demo" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.demo')}
              </a>
              <a 
                href="/contact" 
                className="inline-flex items-center px-6 py-3 rounded-lg border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.contact')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section avec un design plus moderne */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-brand-orange/5" />
        <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" style={{ background: 'radial-gradient(circle at center, rgba(217,111,39,0.05) 0%, rgba(73,107,136,0.05) 100%)' }} />
        
        <div className="container-custom relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-blue to-brand-orange pb-2">
              {t('intro.title')}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t('intro.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid avec des cartes colorées */}
      <section className="py-24 bg-muted/50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 hover:shadow-lg transition-shadow group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <BookOpen className="w-12 h-12 text-brand-blue mb-6" />
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                {t('features.referentials.title')}
              </h3>
              <p className="text-muted-foreground relative z-10">
                {t('features.referentials.description')}
              </p>
            </Card>

            <Card className="p-8 hover:shadow-lg transition-shadow group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Network className="w-12 h-12 text-brand-orange mb-6" />
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                {t('features.translation.title')}
              </h3>
              <p className="text-muted-foreground relative z-10">
                {t('features.translation.description')}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* GPEC Section avec des éléments graphiques */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 via-transparent to-brand-orange/5" />
        <div className="container-custom relative">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <LineChart className="w-12 h-12 text-brand-blue" />
              <h3 className="text-3xl font-semibold text-foreground">
                {t('features.gpec.title')}
              </h3>
            </div>
            <p className="text-lg mb-8 text-muted-foreground">
              {t('features.gpec.description')}
            </p>
            <div className="grid gap-4">
              {gpecBenefits.map((benefit, index) => (
                <Card key={index} className="p-6 group hover:shadow-md transition-all duration-300 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start gap-4 relative z-10">
                    <span className="text-brand-orange font-bold text-xl">0{index + 1}</span>
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RSE Section avec un design moderne */}
      <section className="py-24 bg-muted/50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Users className="w-12 h-12 text-brand-orange" />
              <h3 className="text-3xl font-semibold text-foreground">
                {t('features.rse.title')}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {rseBenefits.map((benefit, index) => (
                <Card key={index} className="p-6 group hover:shadow-md transition-all duration-300 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-start gap-4 relative z-10">
                    <span className="text-brand-blue font-bold text-xl">0{index + 1}</span>
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visualization & Interoperability avec des cartes modernes */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 via-transparent to-brand-blue/5" />
        <div className="container-custom relative">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 hover:shadow-lg transition-shadow group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Database className="w-12 h-12 text-brand-blue mb-6" />
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                {t('features.visualization.title')}
              </h3>
              <p className="text-muted-foreground relative z-10">
                {t('features.visualization.description')}
              </p>
            </Card>

            <Card className="p-8 hover:shadow-lg transition-shadow group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <GitMerge className="w-12 h-12 text-brand-orange mb-6" />
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                {t('features.interoperability.title')}
              </h3>
              <p className="text-muted-foreground relative z-10">
                {t('features.interoperability.description')}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Conclusion avec un design plus impactant */}
      <section className="py-24 bg-muted/50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-foreground mb-12 relative">
              <span className="relative">
                <span className="absolute -inset-1 rounded-lg bg-gradient-to-r from-brand-blue/10 to-brand-orange/10 blur-sm" />
                <span className="relative">{t('conclusion')}</span>
              </span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact?type=demo" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-brand-blue to-brand-orange text-white hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.demo')}
              </a>
              <a 
                href="/contact" 
                className="inline-flex items-center px-6 py-3 rounded-lg border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('cta.contact')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HR;
